import React, { useEffect, useState } from "react";
import { BarChart3, Eye, PencilLine, Inbox, Percent } from "lucide-react";
import api from "../../lib/api";

const PERIODS = ["today", "7d", "30d"];

// Compact booking-page funnel metrics rendered inside the Customer Booking
// Page dashboard card. Best-effort: fetch errors show nothing alarming.
export const BookingPageAnalytics = ({ t }) => {
  const [period, setPeriod] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/booking-page/analytics?period=${period}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  const empty =
    !loading && data && data.page_views === 0 && data.quote_starts === 0 && data.quote_requests === 0;

  const metrics = data
    ? [
        { key: "views", icon: Eye, value: data.page_views, label: t("dash.bpaViews") },
        { key: "starts", icon: PencilLine, value: data.quote_starts, label: t("dash.bpaStarts") },
        { key: "requests", icon: Inbox, value: data.quote_requests, label: t("dash.bpaRequests") },
        { key: "conversion", icon: Percent, value: `${data.conversion_rate}%`, label: t("dash.bpaConversion") },
      ]
    : [];

  return (
    <div className="mt-5 pt-5 border-t border-gray-100" data-testid="booking-page-analytics">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
        <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
          <BarChart3 className="w-4 h-4 text-green-800" /> {t("dash.bpaTitle")}
        </p>
        <div className="flex gap-1" data-testid="bpa-period-selector">
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                period === p
                  ? "bg-green-800 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              data-testid={`bpa-period-${p}`}
            >
              {t(`dash.bpaPeriod.${p}`)}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-16 rounded-xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : empty ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/70 px-4 py-4 text-center" data-testid="bpa-empty-state">
          <p className="text-sm font-medium text-gray-700">{t("dash.bpaEmpty")}</p>
          <p className="mt-1 text-xs text-gray-500">{t("dash.bpaEmptyHint")}</p>
        </div>
      ) : data ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {metrics.map((m) => (
            <div
              key={m.key}
              className="rounded-xl border border-gray-200 bg-gray-50/70 px-3 py-2.5"
              data-testid={`bpa-metric-${m.key}`}
            >
              <p className="font-display text-xl font-semibold text-gray-900 tabular-nums">{m.value}</p>
              <p className="text-[11px] text-gray-500 leading-snug flex items-center gap-1">
                <m.icon className="w-3 h-3 shrink-0" /> {m.label}
              </p>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
